import {
  AlertTriangle,
  ArrowLeft,
  LoaderCircle,
  Maximize,
  Minimize,
  RefreshCw,
  RotateCw,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { getPlayerDataService } from "../data/playerDataService";
import { type GameFinishedEvent, parseGameBridgeEvent } from "../gameBridge";

const GAME_URL = "/game/index.html";
const GAME_BUILD_VERSION = "phase-c-1";
const GAME_READY_TIMEOUT_MS = 30_000;

type FullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type RuntimeState = "connecting" | "ready" | "playing" | "finished" | "error";
type SaveState = "idle" | "saving" | "saved" | "error";

function runtimeStatusLabel(
  runtimeState: RuntimeState,
  result: GameFinishedEvent["payload"] | null,
) {
  if (runtimeState === "ready") {
    return "Játékra kész";
  }
  if (runtimeState === "playing") {
    return "Játék folyamatban";
  }
  if (runtimeState === "finished" && result) {
    const outcome = result.outcome === "won" ? "Pálya teljesítve" : "Játék vége";
    return `${outcome} · ${result.score.toLocaleString("hu-HU")} pont`;
  }
  if (runtimeState === "error") {
    return "Kapcsolati hiba";
  }
  return "Kapcsolódás a játékhoz";
}

export function GamePage() {
  const auth = useAuth();
  const pageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const finishedSessionIdsRef = useRef(new Set<string>());
  const pendingResultRef = useRef<GameFinishedEvent["payload"] | null>(null);
  const saveAttemptRef = useRef(0);
  const dataServiceRef = useRef(getPlayerDataService());
  const [frameKey, setFrameKey] = useState(0);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [runtimeState, setRuntimeState] = useState<RuntimeState>("connecting");
  const [lastResult, setLastResult] = useState<GameFinishedEvent["payload"] | null>(null);
  const [loadErrorMessage, setLoadErrorMessage] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenMessage, setFullscreenMessage] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveMessage, setSaveMessage] = useState("");

  const saveGameResult = useCallback(
    async (result: GameFinishedEvent["payload"]) => {
      pendingResultRef.current = result;
      const attempt = ++saveAttemptRef.current;

      if (!auth.account) {
        setSaveState("error");
        setSaveMessage("Az eredmény mentéséhez vendég- vagy Google-játékosprofil szükséges.");
        return;
      }
      if (!dataServiceRef.current) {
        setSaveState("error");
        setSaveMessage("Ehhez a buildhez nincs Firebase adatmentés beállítva.");
        return;
      }

      setSaveState("saving");
      setSaveMessage("Eredmény mentése...");
      try {
        const response = await dataServiceRef.current.submitGameResult(result);
        if (attempt !== saveAttemptRef.current) {
          return;
        }
        setSaveState("saved");
        setSaveMessage(
          response.alreadySaved
            ? "Ez az eredmény már el volt mentve."
            : `Eredmény elmentve · legjobb: ${response.bestScore.toLocaleString("hu-HU")} pont`,
        );
      } catch (error) {
        if (attempt !== saveAttemptRef.current) {
          return;
        }
        setSaveState("error");
        setSaveMessage(
          error instanceof Error ? error.message : "Az eredményt nem sikerült elmenteni.",
        );
      }
    },
    [auth.account],
  );

  useEffect(() => {
    const updateFullscreenState = () => {
      const fullscreenDocument = document as FullscreenDocument;
      setIsFullscreen(Boolean(document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement));
    };

    document.addEventListener("fullscreenchange", updateFullscreenState);
    document.addEventListener("webkitfullscreenchange", updateFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", updateFullscreenState);
      document.removeEventListener("webkitfullscreenchange", updateFullscreenState);
    };
  }, []);

  useEffect(() => {
    const handleGameMessage = (event: MessageEvent<unknown>) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== frameRef.current?.contentWindow
      ) {
        return;
      }

      const gameEvent = parseGameBridgeEvent(event.data);
      if (!gameEvent) {
        return;
      }

      setLoadState("ready");
      setLoadErrorMessage("");

      if (gameEvent.type === "gameReady") {
        setRuntimeState("ready");
        return;
      }

      if (gameEvent.type === "gameStarted") {
        saveAttemptRef.current += 1;
        activeSessionIdRef.current = gameEvent.payload.gameSessionId;
        pendingResultRef.current = null;
        setLastResult(null);
        setSaveState("idle");
        setSaveMessage("");
        setRuntimeState("playing");
        return;
      }

      const { gameSessionId } = gameEvent.payload;
      if (
        activeSessionIdRef.current !== gameSessionId ||
        finishedSessionIdsRef.current.has(gameSessionId)
      ) {
        return;
      }

      finishedSessionIdsRef.current.add(gameSessionId);
      setLastResult(gameEvent.payload);
      setRuntimeState("finished");
      void saveGameResult(gameEvent.payload);
    };

    window.addEventListener("message", handleGameMessage);
    return () => window.removeEventListener("message", handleGameMessage);
  }, [saveGameResult]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setLoadState((current) => {
        if (current !== "loading") {
          return current;
        }
        setLoadErrorMessage("A játékmotor nem válaszolt időben.");
        setRuntimeState("error");
        return "error";
      });
    }, GAME_READY_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [frameKey]);

  const reloadGame = useCallback(() => {
    saveAttemptRef.current += 1;
    activeSessionIdRef.current = null;
    finishedSessionIdsRef.current.clear();
    pendingResultRef.current = null;
    setLoadState("loading");
    setRuntimeState("connecting");
    setLastResult(null);
    setLoadErrorMessage("");
    setFullscreenMessage("");
    setSaveState("idle");
    setSaveMessage("");
    setFrameKey((current) => current + 1);
  }, []);

  const handleFrameError = useCallback(() => {
    setLoadErrorMessage("A játékfájlok nem töltődtek be.");
    setRuntimeState("error");
    setLoadState("error");
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const fullscreenDocument = document as FullscreenDocument;
    const fullscreenElement =
      document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement;

    try {
      if (fullscreenElement) {
        const exitFullscreen =
          document.exitFullscreen?.bind(document) ??
          fullscreenDocument.webkitExitFullscreen?.bind(document);
        await exitFullscreen?.();
        return;
      }

      const page = pageRef.current as FullscreenElement | null;
      const requestFullscreen =
        page?.requestFullscreen?.bind(page) ?? page?.webkitRequestFullscreen?.bind(page);

      if (!requestFullscreen) {
        setFullscreenMessage("Ez a böngésző nem támogatja a teljes képernyős módot.");
        return;
      }

      await requestFullscreen();
      setFullscreenMessage("");

      const orientation = screen.orientation as
        | (ScreenOrientation & {
            lock?: (orientation: "landscape") => Promise<void>;
          })
        | undefined;
      await orientation?.lock?.("landscape").catch(() => undefined);
    } catch {
      setFullscreenMessage("A teljes képernyős mód nem indult el. Próbáld meg újra a gombbal.");
    }
  }, []);

  return (
    <div className="game-page" ref={pageRef}>
      <div className="game-toolbar">
        <Link to="/play" className="button button--quiet">
          <ArrowLeft aria-hidden="true" size={19} /> Vissza
        </Link>
        <div className="game-toolbar__status" aria-live="polite">
          <span>World 1-1: Tokyo Ruins</span>
          <small>{runtimeStatusLabel(runtimeState, lastResult)}</small>
        </div>
        <div className="game-toolbar__actions">
          <button
            className="icon-button"
            type="button"
            aria-label="Játék újratöltése"
            title="Játék újratöltése"
            onClick={reloadGame}
          >
            <RefreshCw aria-hidden="true" size={19} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label={isFullscreen ? "Kilépés a teljes képernyőből" : "Teljes képernyő"}
            title={isFullscreen ? "Kilépés a teljes képernyőből" : "Teljes képernyő"}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize aria-hidden="true" size={20} />
            ) : (
              <Maximize aria-hidden="true" size={20} />
            )}
          </button>
        </div>
      </div>

      <section
        className="game-stage"
        aria-label="Beágyazott Zsombzilla játék"
        onPointerDown={() => frameRef.current?.focus()}
      >
        <iframe
          key={frameKey}
          ref={frameRef}
          className="game-stage__frame"
          src={`${GAME_URL}?v=${GAME_BUILD_VERSION}&instance=${frameKey}`}
          title="Zsombzilla játék"
          allow="autoplay; fullscreen"
          allowFullScreen
          onError={handleFrameError}
        />

        {loadState === "loading" && (
          <div className="game-stage__notice game-stage__notice--loading" role="status">
            <LoaderCircle className="game-stage__spinner" aria-hidden="true" size={30} />
            <h1>Játékmotor indítása</h1>
            <p>Betöltjük Zsombzilla világát és a pályát.</p>
          </div>
        )}

        {loadState === "error" && (
          <div className="game-stage__notice game-stage__notice--error" role="alert">
            <AlertTriangle aria-hidden="true" size={30} />
            <h1>A játék nem töltődött be</h1>
            <p>{loadErrorMessage || "Ellenőrizd a kapcsolatot, majd próbáld újra."}</p>
            <button className="button button--primary" type="button" onClick={reloadGame}>
              <RefreshCw aria-hidden="true" size={18} /> Újrapróbálás
            </button>
          </div>
        )}
      </section>

      <div className="game-footer">
        {saveState !== "idle" && (
          <aside className={`game-save-status game-save-status--${saveState}`} aria-live="polite">
            {saveState === "saving" && (
              <LoaderCircle className="spin" aria-hidden="true" size={18} />
            )}
            {saveState === "error" && <AlertTriangle aria-hidden="true" size={18} />}
            <span>{saveMessage}</span>
            {saveState === "error" && pendingResultRef.current && (
              <button
                className="button button--quiet"
                type="button"
                onClick={() => void saveGameResult(pendingResultRef.current!)}
              >
                Újrapróbálás
              </button>
            )}
          </aside>
        )}

        <aside className="rotate-hint" aria-live="polite">
          <RotateCw aria-hidden="true" size={22} />
          <span>
            {fullscreenMessage ||
              "Telefonon használd a teljes képernyő gombot, majd fordítsd fekvő helyzetbe a készüléket."}
          </span>
        </aside>
      </div>
    </div>
  );
}
